import { useForm, useWatch } from "react-hook-form";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  SUPPORTED_HARDWARE,
  hardwareFor,
  updateDeviceStatusSchema,
  type Device,
  type UpdateDeviceRequest,
} from "@/contracts";
import { z } from "zod";
import { useTranslation } from "react-i18next";

import {
  assetOptionsQuery,
  featuresQuery,
  isHardwareEnabledInMap,
  siteOptionsQuery,
  updateDeviceMutation,
} from "@/api/queries";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardBody, CardHeader } from "@/components/ui/card";
import { SelectField, TextField } from "@/components/ui/field";
import { useApiClient } from "@/lib/api-context";
import { ApiError } from "@/lib/api-error";
import { DEVICE_STATUS } from "./device-labels";
import { HardwareSelectField } from "./hardware-select-field";

/**
 * Form shape, not the wire shape.
 *
 * `UpdateDeviceRequest` is a partial with nullables because a PATCH may carry
 * one field; a form always has every control filled in, with the empty string
 * standing in for "none". The two are reconciled in `toPatch` below.
 */
const editFormSchema = z.object({
  /**
   * Empty means "leave it alone", which is not the same as unset.
   *
   * A device recorded before its hardware left the registry has nothing to
   * select, and forcing a choice would make an unrelated edit — moving the
   * device to another asset, say — silently rewrite what hardware it is.
   */
  hardwareId: z.string(),
  supplier: z.string().trim().max(60),
  status: updateDeviceStatusSchema,
  assetId: z.string(),
  siteId: z.string(),
  loraApplicationId: z.string().trim().max(120),
  loraDeviceId: z.string().trim().max(120),
});

type EditFormValues = z.infer<typeof editFormSchema>;

export function DeviceEditForm({ device, onDone }: { device: Device; onDone: () => void }) {
  const { t } = useTranslation("pages");
  const { t: tDevices } = useTranslation("devices");
  const client = useApiClient();
  const queryClient = useQueryClient();
  const sites = useQuery(siteOptionsQuery(client, device.organizationId));
  const assets = useQuery(assetOptionsQuery(client, device.organizationId));
  const features = useQuery(featuresQuery(client));
  const currentHardware = hardwareFor(device.manufacturer, device.model);
  const enabledHardware = features.data?.enabledHardware;
  // Keep the device's own model in the list even when the flag is off, so an
  // edit of something else does not force a hardware change.
  const hardwareOptions = SUPPORTED_HARDWARE.filter(
    (hardware) =>
      hardware.id === currentHardware?.id ||
      isHardwareEnabledInMap(enabledHardware, hardware.id),
  );

  // False for a device registered before its model left the registry, which
  // the form has to keep editable rather than refuse.
  const recognised = currentHardware !== undefined;

  const {
    register,
    handleSubmit,
    control,
    setError,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<EditFormValues>({
    resolver: zodResolver(editFormSchema),
    defaultValues: {
      hardwareId: currentHardware?.id ?? "",
      supplier: device.supplier ?? "",
      // PENDING is not offered: a device that reported cannot go back to
      // never having been provisioned, and the API rejects it.
      status: device.status === "PENDING" ? "ACTIVE" : device.status,
      assetId: device.assetId ?? "",
      siteId: device.siteId ?? "",
      loraApplicationId: device.loraApplicationId ?? "",
      loraDeviceId: device.loraDeviceId ?? "",
    },
  });

  const mutation = useMutation({
    ...updateDeviceMutation(client, device.id),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["devices"] });
      await queryClient.invalidateQueries({ queryKey: ["dashboard"] });
      onDone();
    },
  });

  // `useWatch` rather than `watch()`: it yields the value itself, which the
  // React compiler can reason about, instead of a function it has to skip.
  const selectedAssetId = useWatch({ control, name: "assetId" });
  const selectedAsset = assets.data?.data.find((asset) => asset.id === selectedAssetId);
  const hardwareId = useWatch({ control, name: "hardwareId" });
  const status = useWatch({ control, name: "status" });
  const siteId = useWatch({ control, name: "siteId" });

  const onSubmit = handleSubmit(async (values) => {
    const patch = toPatch(device, values);

    if (Object.keys(patch).length === 0) {
      onDone();

      return;
    }

    try {
      await mutation.mutateAsync(patch);
    } catch (error) {
      if (error instanceof ApiError) {
        for (const [field, message] of Object.entries(error.fieldErrors())) {
          if (field in editFormSchema.shape) {
            setError(field as keyof EditFormValues, { message });
          }
        }
      }
    }
  });

  return (
    <Card>
      <CardHeader title={tDevices("editTitle")} description={tDevices("editDescription")} />
      <CardBody>
        <form onSubmit={(event) => void onSubmit(event)} noValidate className="flex flex-col gap-4">
          {mutation.isError ? <Alert>{mutation.error.message}</Alert> : null}

          <div className="grid gap-4 sm:grid-cols-2">
            <HardwareSelectField
              label={tDevices("fieldHardware")}
              hint={
                recognised
                  ? tDevices("hintHardwareChange")
                  : tDevices("hintHardwareUnsupported", {
                      manufacturer: device.manufacturer,
                      model: device.model,
                    })
              }
              placeholder={tDevices("chooseHardware")}
              allowEmpty={!recognised}
              {...(recognised ? {} : { emptyOptionLabel: tDevices("keepAsRecorded") })}
              {...(errors.hardwareId?.message === undefined
                ? {}
                : { error: errors.hardwareId.message })}
              value={hardwareId}
              onChange={(value) =>
                setValue("hardwareId", value, { shouldValidate: true, shouldDirty: true })
              }
              options={hardwareOptions}
            />

            <TextField
              label={tDevices("fieldSupplier")}
              hint={tDevices("hintSupplier")}
              {...(errors.supplier?.message === undefined
                ? {}
                : { error: errors.supplier.message })}
              {...register("supplier")}
            />

            <SelectField
              label={tDevices("fieldStatus")}
              value={status}
              onChange={(value) =>
                setValue("status", value as EditFormValues["status"], {
                  shouldValidate: true,
                  shouldDirty: true,
                })
              }
              data={updateDeviceStatusSchema.options.map((option) => ({
                value: option,
                label: DEVICE_STATUS[option].label,
              }))}
            />

            <SelectField
              label={tDevices("fieldAsset")}
              hint={tDevices("hintAsset")}
              value={selectedAssetId}
              onChange={(value) =>
                setValue("assetId", value, { shouldValidate: true, shouldDirty: true })
              }
              data={[
                { value: "", label: tDevices("unassigned") },
                ...(assets.data?.data ?? []).map((asset) => ({
                  value: asset.id,
                  label: `${asset.name} · ${asset.siteName}`,
                })),
              ]}
            />

            <SelectField
              label={tDevices("fieldSite")}
              // A device attached to an asset takes that asset's site
              // (ADR-026); letting both be set would allow them to disagree.
              disabled={selectedAssetId !== ""}
              hint={
                selectedAsset
                  ? tDevices("hintSiteFromAsset", { siteName: selectedAsset.siteName })
                  : tDevices("hintSite")
              }
              value={siteId}
              onChange={(value) =>
                setValue("siteId", value, { shouldValidate: true, shouldDirty: true })
              }
              data={[
                { value: "", label: tDevices("unassigned") },
                ...(sites.data?.data ?? []).map((site) => ({
                  value: site.id,
                  label: site.name,
                })),
              ]}
            />

            <TextField
              label={tDevices("fieldLoraApp")}
              {...(errors.loraApplicationId?.message === undefined
                ? {}
                : { error: errors.loraApplicationId.message })}
              {...register("loraApplicationId")}
            />

            <TextField
              label={tDevices("fieldLoraDevice")}
              {...(errors.loraDeviceId?.message === undefined
                ? {}
                : { error: errors.loraDeviceId.message })}
              {...register("loraDeviceId")}
            />
          </div>

          <div className="flex gap-2">
            <Button type="submit" loading={isSubmitting || mutation.isPending}>
              {t("saveChanges")}
            </Button>
            <Button variant="secondary" onClick={onDone}>
              {t("cancel")}
            </Button>
          </div>
        </form>
      </CardBody>
    </Card>
  );
}

/**
 * Sends only what actually changed.
 *
 * The API rejects an empty PATCH and writes an audit entry for every field
 * listed, so resubmitting the whole form would fill the trail with edits that
 * changed nothing.
 */
function toPatch(device: Device, values: EditFormValues): UpdateDeviceRequest {
  const patch: UpdateDeviceRequest = {};

  const currentHardwareId = hardwareFor(device.manufacturer, device.model)?.id ?? "";

  if (values.hardwareId !== "" && values.hardwareId !== currentHardwareId) {
    patch.hardwareId = values.hardwareId as NonNullable<UpdateDeviceRequest["hardwareId"]>;
  }

  const supplier = values.supplier === "" ? null : values.supplier;

  if (supplier !== device.supplier) {
    patch.supplier = supplier;
  }

  if (values.status !== device.status) {
    patch.status = values.status;
  }

  const assetId = values.assetId === "" ? null : values.assetId;

  if (assetId !== device.assetId) {
    patch.assetId = assetId;
  }

  // Only meaningful with no asset; with one, the server derives the site.
  if (assetId === null) {
    const siteId = values.siteId === "" ? null : values.siteId;

    if (siteId !== device.siteId) {
      patch.siteId = siteId;
    }
  }

  const loraApplicationId = values.loraApplicationId === "" ? null : values.loraApplicationId;

  if (loraApplicationId !== device.loraApplicationId) {
    patch.loraApplicationId = loraApplicationId;
  }

  const loraDeviceId = values.loraDeviceId === "" ? null : values.loraDeviceId;

  if (loraDeviceId !== device.loraDeviceId) {
    patch.loraDeviceId = loraDeviceId;
  }

  return patch;
}
